import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { deleteToken as deleteMessagingToken, getToken } from 'firebase/messaging';
import type { RootState } from 'redux/store';
import rollbar from 'rollbarClient';
import { useDeleteFcmTokenMutation, useRegisterFcmTokenMutation } from 'services/api/user.api';
import { removeFcmUnregister, setFcmUnregister } from 'services/fcmTokenStore';
import { messaging, vapidKey } from 'services/firebase/firebase';
import { canUseCookieFirstCategory, getCachedConsent, getCookieFirstConsent } from 'utils/cookieFirst';

const FCM_DISMISS_STORAGE_KEY = 'fcm_permission_dismissed_until';
const FCM_PUSH_ENABLED_KEY = 'fcm_push_enabled';
const FCM_PERMISSION_STATE_KEY = 'fcm_notification_permission_state';
const FCM_TOKEN_STORAGE_KEY = 'fcm_token';
const FCM_TOKEN_LAST_SYNCED_AT_KEY = 'fcm_token_last_synced_at';
const FCM_TOKEN_SYNCED_USER_ID_KEY = 'fcm_token_synced_user_id';
const FCM_TOKEN_SYNC_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;
let syncInFlightPromise: Promise<string | null> | null = null;

type FcmKnownPermission = NotificationPermission | null;

export function getStoredFcmToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
}

export function clearStoredFcmToken(): void {
  localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
  localStorage.removeItem(FCM_TOKEN_LAST_SYNCED_AT_KEY);
  localStorage.removeItem(FCM_TOKEN_SYNCED_USER_ID_KEY);
}

function getLastTokenSync(): { syncedAt: number; userId: number | null } {
  try {
    const syncedAt = Number(localStorage.getItem(FCM_TOKEN_LAST_SYNCED_AT_KEY));
    const userId = Number(localStorage.getItem(FCM_TOKEN_SYNCED_USER_ID_KEY));
    return {
      syncedAt: Number.isFinite(syncedAt) ? syncedAt : 0,
      userId: Number.isSafeInteger(userId) && userId > 0 ? userId : null,
    };
  } catch {
    return { syncedAt: 0, userId: null };
  }
}

function storeTokenSync(token: string, userId: number, syncedAt: number): void {
  try {
    localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
    localStorage.setItem(FCM_TOKEN_LAST_SYNCED_AT_KEY, String(syncedAt));
    localStorage.setItem(FCM_TOKEN_SYNCED_USER_ID_KEY, String(userId));
  } catch {
    // ignore
  }
}

export function isFcmPushEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return localStorage.getItem(FCM_PUSH_ENABLED_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setFcmPushEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(FCM_PUSH_ENABLED_KEY, enabled ? '1' : '0');
  } catch {
    // ignore
  }
}

export function getFcmPermissionState(): FcmKnownPermission {
  try {
    const raw = localStorage.getItem(FCM_PERMISSION_STATE_KEY);
    return raw === 'default' || raw === 'granted' || raw === 'denied' ? raw : null;
  } catch {
    return null;
  }
}

export function setFcmPermissionState(permissionState: FcmKnownPermission): void {
  try {
    if (permissionState) {
      localStorage.setItem(FCM_PERMISSION_STATE_KEY, permissionState);
    } else {
      localStorage.removeItem(FCM_PERMISSION_STATE_KEY);
    }
  } catch {
    // ignore
  }
}

export function setFcmPermissionDismissedUntil(ms: number): void {
  try {
    localStorage.setItem(FCM_DISMISS_STORAGE_KEY, String(Date.now() + ms));
  } catch {
    // ignore
  }
}

export function getFcmPermissionDismissedUntil(): number {
  try {
    const v = localStorage.getItem(FCM_DISMISS_STORAGE_KEY);
    return v ? Number(v) : 0;
  } catch {
    return 0;
  }
}

export function useFcmToken() {
  const currentUserId = useSelector((state: RootState) => state.user.id);
  const tokenRef = useRef<string | null>(null);
  const [registerFcmToken] = useRegisterFcmTokenMutation();
  const [deleteFcmToken] = useDeleteFcmTokenMutation();

  const getServiceWorkerRegistration = useCallback(
    async (serviceWorkerRegistration?: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration | null> => {
      if (serviceWorkerRegistration) return serviceWorkerRegistration;
      if (!('serviceWorker' in navigator)) return null;

      const existing = await navigator.serviceWorker.getRegistration('/sw.js');
      if (existing) return existing;

      try {
        // navigator.serviceWorker.ready never rejects/times out — hangs forever if SW
        // fails to activate (e.g. install error, private browsing quirks). 8s is generous
        // given normal activation takes 1-3s; bail out rather than block token registration.
        return await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<null>((resolve) => {
            window.setTimeout(() => resolve(null), 8000);
          }),
        ]);
      } catch {
        return null;
      }
    },
    []
  );

  const registerToken = useCallback(
    async (serviceWorkerRegistration?: ServiceWorkerRegistration): Promise<string | null> => {
      if (!vapidKey) return null;
      const messagingInstance = await messaging;
      if (!messagingInstance) return null;
      if (Notification.permission !== 'granted') return null;
      const cachedConsent = getCachedConsent();
      if (!isFcmPushEnabled() || !canUseCookieFirstCategory('functional', cachedConsent ?? getCookieFirstConsent())) {
        return null;
      }

      const registration = await getServiceWorkerRegistration(serviceWorkerRegistration);
      if (!registration) return null;
      try {
        const token = await getToken(messagingInstance, {
          vapidKey,
          serviceWorkerRegistration: registration,
        });
        if (token) {
          const now = Date.now();
          const previousToken = getStoredFcmToken();
          const isSameToken = previousToken === token;
          const lastSync = getLastTokenSync();
          const shouldSyncWithBackend =
            !isSameToken || lastSync.userId !== currentUserId || now - lastSync.syncedAt >= FCM_TOKEN_SYNC_INTERVAL_MS;
          if (previousToken && previousToken !== token) {
            deleteFcmToken({ token: previousToken })
              .unwrap()
              .catch(() => {});
          }
          tokenRef.current = token;

          if (shouldSyncWithBackend) {
            await registerFcmToken({ token }).unwrap();
            storeTokenSync(token, currentUserId, now);
          }
          return token;
        }
      } catch (error) {
        rollbar.error('FCM token synchronization failed', error as Error);
      }
      return null;
    },
    [currentUserId, deleteFcmToken, getServiceWorkerRegistration, registerFcmToken]
  );

  const revokeBrowserToken = useCallback(async (): Promise<void> => {
    const messagingInstance = await messaging;
    if (messagingInstance) {
      await Promise.allSettled([deleteMessagingToken(messagingInstance)]);
    }

    tokenRef.current = null;
    clearStoredFcmToken();
  }, []);

  const unregisterTokenNow = useCallback(async (): Promise<void> => {
    const token = tokenRef.current ?? getStoredFcmToken();
    const cleanupTasks: Promise<unknown>[] = [revokeBrowserToken()];

    if (token) {
      cleanupTasks.push(deleteFcmToken({ token }).unwrap());
    }

    await Promise.allSettled(cleanupTasks);
  }, [deleteFcmToken, revokeBrowserToken]);

  const syncFcmRegistration = useCallback(async (): Promise<string | null> => {
    if (syncInFlightPromise) {
      return syncInFlightPromise;
    }

    syncInFlightPromise = (async () => {
      const canUseNotifications = typeof window !== 'undefined' && 'Notification' in window;
      const cachedConsent = getCachedConsent();
      const shouldBeRegistered =
        canUseNotifications &&
        Notification.permission === 'granted' &&
        isFcmPushEnabled() &&
        canUseCookieFirstCategory('functional', cachedConsent ?? getCookieFirstConsent());

      if (!shouldBeRegistered) {
        await unregisterTokenNow();
        return null;
      }

      return registerToken();
    })().finally(() => {
      syncInFlightPromise = null;
    });

    return syncInFlightPromise;
  }, [registerToken, unregisterTokenNow]);

  const waitForInFlightSync = useCallback(async (): Promise<void> => {
    const inFlightSync = syncInFlightPromise;
    if (inFlightSync) {
      await inFlightSync.catch(() => null);
    }
  }, []);

  const unregisterToken = useCallback(async (): Promise<void> => {
    await waitForInFlightSync();
    await unregisterTokenNow();
  }, [unregisterTokenNow, waitForInFlightSync]);

  const revokeBrowserRegistration = useCallback(async (): Promise<void> => {
    await waitForInFlightSync();
    await revokeBrowserToken();
  }, [revokeBrowserToken, waitForInFlightSync]);

  const unregisterRef = useRef(unregisterToken);
  unregisterRef.current = unregisterToken;
  useEffect(() => {
    const unregister = () => unregisterRef.current();
    const revokeLocal = () => revokeBrowserRegistration();
    setFcmUnregister(unregister, revokeLocal);
    return () => removeFcmUnregister(unregister);
  }, [revokeBrowserRegistration]);

  return { registerToken, unregisterToken, syncFcmRegistration, currentTokenRef: tokenRef };
}
